import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IconChevronLeft24, IconChevronRight24 } from '@fabric-ds/icons/react';
import cn from 'classnames';

import { trackClick } from '@/lib/tracking/tracking';
import setupLightbox from '@/lib/gallery/lightbox';
import ImageCarousel from '@/lib/gallery/imageCarousel';

import styles from './styles.module.css';

export function ImageList({ adId, heading, images }) {
    const lightboxRef = useRef(null);
    const imageRef = useRef(null);
    const trackedImageview = useRef(false);
    const [carousel, setCarousel] = useState();
    const hasImages = Boolean(images?.length);

    useEffect(() => {
        if (hasImages) {
            const updatedCarousel = new ImageCarousel(imageRef.current);
            setCarousel(updatedCarousel);
            updatedCarousel.preload(1); // preload one before and one after
            updatedCarousel.update(imageRef.current);
            setupLightbox(lightboxRef.current);
        }
    }, [hasImages, setCarousel]);

    useEffect(() => {
        if (carousel) {
            document.body.addEventListener('keydown', handleKeydown);

            return () => {
                document.body.removeEventListener('keydown', handleKeydown);
            };
        }
    }, [carousel]);

    const handleKeydown = (event) => {
        if (event.target.closest('input, textarea') || event.metaKey || event.altKey || event.shiftKey || event.ctrlKey) {
            return;
        }

        switch (event.keyCode) {
            case 37: // Left arrow
                handlePrevious(event);
                break;
            case 39: // Right arrow
                handleNext(event);
                break;
        }
    };

    const handlePrevious = (event) => {
        event.stopPropagation();
        carousel.previous();
        trackImageView();
    };

    const handleNext = (event) => {
        event.stopPropagation();
        carousel.next();
        trackImageView();
    };

    const trackImageView = () => {
        // only track the first click on left/right arrows
        if (!trackedImageview.current) {
            trackPulse('View', 'View carousel image');
            trackedImageview.current = true;
        }
    };

    const trackPulse = (intent, name) => {
        trackClick({
            type: 'Click',
            intent: intent,
            name: name,
            object: {
                id: 'open_image_gallery',
                type: 'UIElement',
                elementType: 'Image',
                items: [
                    {
                        type: 'ClassifiedAd',
                        id: adId,
                        contentId: adId.toString(),
                        name: heading,
                        category: 'travel > holiday rental',
                    },
                ],
                hasExtendedProfile: false,
            },
            vertical: {
                name: 'travel',
                subVertical: 'holiday_rental',
            },
        });
    };

    const poorResolution = (image) => image.height < 768 && image.width < 768;

    const renderHiddenImages = () =>
        images.map((image, index) => (
            <img
                key={`hidden-image-${image.src}`}
                data-src={image.src}
                data-srcset={image.srcSet}
                height={image.height}
                width={image.width}
                data-thumb-src={image.thumb155hSrc}
                data-gallerylightbox-name="ad"
                aria-label={image.description}
                alt=""
                data-testid={`ObjectImages-hiddenImage-${index}`}
            />
        ));

    const renderImages = () =>
        images.map((image, index) => (
            <a
                href={`/gallery.html?finnkode=${adId}#image${index}`}
                key={`image-${image.src}`}
                style={{ display: index === 0 ? 'block' : 'none' }}
                data-testid={`ObjectImages-imageLink-${index}`}>
                {index === 0 ? (
                    <img
                        className={cn(styles.imageItemImg, 'img-format__img rounded-8')}
                        sizes={`${
                            poorResolution(image) ? '(min-width: 768px) 768px' : '(min-width: 1200px) 80vw, (min-width: 768px) 100vw'
                        }`}
                        alt={image.description}
                        aria-label="Galleribilde"
                        src={image.src}
                        srcSet={image.srcSet}
                        data-index={index}
                        data-testid={`ObjectImages-image-${index}`}
                    />
                ) : (
                    <img
                        className={cn(styles.imageItemImg, 'img-format__img rounded-8')}
                        sizes={`${
                            poorResolution(image) ? '(min-width: 768px) 768px' : '(min-width: 1200px) 80vw, (min-width: 768px) 100vw'
                        }`}
                        alt={image.description}
                        aria-label="Galleribilde"
                        data-src={image.src}
                        data-srcset={image.srcSet}
                        data-index={index}
                        data-testid={`ObjectImages-image-${index}`}
                    />
                )}
                <p className="absolute bottom-0 w-full text-center">
                    <span className="px-8 py-4 bg-gray-500 text-white rounded-4">
                        ({index + 1}/{images.length}){image.description}
                    </span>
                </p>
            </a>
        ));

    const renderImageContainer = () => (
        <div onClick={() => trackPulse('Open', 'Open image gallery')}>
            <div ref={imageRef}>{renderImages()}</div>
            <button
                className={cn(styles.button, 'button button--pill absolute top-1/2 right-16 text-white')}
                type="button"
                aria-label="Neste bilde"
                data-next="true"
                data-testid="nextButton"
                onClick={handleNext}>
                <IconChevronRight24 />
            </button>
            <button
                className={cn(styles.button, 'button button--pill absolute top-1/2 left-16 text-white')}
                type="button"
                aria-label="Forrige bilde"
                data-prev="true"
                data-testid="previousButton"
                onClick={handlePrevious}>
                <IconChevronLeft24 />
            </button>
        </div>
    );

    const renderNoImages = () => (
        <img
            src="https://static.finncdn.no/_c/mfinn/static/images/no_image_universal.4e750c62.png"
            className="img-format__img rounded-8 mx-auto"
            width="320"
            height="213"
            data-testid="defaultImage"
            alt="Bilde mangler"
        />
    );

    return (
        <div className="md:col-span-3 relative" data-testid="adImageContainer" tabIndex={-1}>
            <div
                className="img-format img-format--ratio4by3 img-format--centered"
                tabIndex={-1}
                data-gallerylightbox-name="ad"
                data-gallerylightbox-context="mobile"
                ref={lightboxRef}>
                {hasImages ? renderImageContainer() : renderNoImages()}
            </div>
            <div className="hidden">{hasImages && renderHiddenImages()}</div>
        </div>
    );
}

ImageList.propTypes = {
    adId: PropTypes.number.isRequired,
    heading: PropTypes.string.isRequired,
    images: PropTypes.array.isRequired,
};
